"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DEFAULT_COMPANY } from "@/lib/company-constants";

export const COMPANY_HEADER = "x-strategyiq-company";
const STORAGE_KEY = "strategyiq_active_company";

export interface CompanyInfo {
  id: string;
  name: string;
  legalName?: string;
  industry: string;
  hq: string;
  fte: number;
  description: string;
  custom?: boolean;
  createdAt?: string;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  department: string;
  companyId: string;
  /** JWT role; Developer Testbed requires `platform_admin` (T20). */
  role?: string;
}

interface CompanyContextValue {
  companyId: string;
  company: CompanyInfo | null;
  companies: CompanyInfo[];
  user: SessionUser | null;
  setCompanyId: (id: string) => void;
  reloadCompanies: () => Promise<CompanyInfo[]>;
  reloadUser: () => Promise<SessionUser | null>;
  logout: () => Promise<void>;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  refreshKey: number;
  bumpRefresh: () => void;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({
  children,
  initialUser = null,
}: {
  children: React.ReactNode;
  /** Server-seeded session so sidebar categories appear on first paint */
  initialUser?: SessionUser | null;
}) {
  const [companyId, setCompanyIdState] = useState(
    () => initialUser?.companyId ?? DEFAULT_COMPANY,
  );
  const [companies, setCompanies] = useState<CompanyInfo[]>([]);
  const [user, setUser] = useState<SessionUser | null>(initialUser);
  const [refreshKey, setRefreshKey] = useState(0);

  const reloadCompanies = useCallback(async () => {
    const res = await fetch("/api/companies", { credentials: "include" });
    const data = await res.json();
    const list: CompanyInfo[] = data.companies ?? [];
    setCompanies(list);
    return list;
  }, []);

  const reloadUser = useCallback(async () => {
    const res = await fetch("/api/auth/me", {
      credentials: "include",
      cache: "no-store",
    });
    const data = await res.json();
    const u = data.user as SessionUser | null;
    setUser(u);
    if (u?.companyId) {
      setCompanyIdState(u.companyId);
      localStorage.setItem(STORAGE_KEY, u.companyId);
    }
    return u;
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) setCompanyIdState(stored);
    reloadCompanies().catch(() => {});
    reloadUser().catch(() => {});
  }, [reloadCompanies, reloadUser]);

  const setCompanyId = useCallback((id: string) => {
    setCompanyIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
    setRefreshKey((k) => k + 1);
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } finally {
      setUser(null);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      window.location.assign("/login");
    }
  }, []);

  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const company = useMemo(
    () => companies.find((c) => c.id === companyId) ?? null,
    [companies, companyId],
  );

  const apiFetch = useCallback(
    (input: RequestInfo | URL, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      headers.set(COMPANY_HEADER, companyId);
      return fetch(input, { ...init, headers, credentials: "include" });
    },
    [companyId],
  );

  const value = useMemo(
    () => ({
      companyId,
      company,
      companies,
      user,
      setCompanyId,
      reloadCompanies,
      reloadUser,
      logout,
      apiFetch,
      refreshKey,
      bumpRefresh,
    }),
    [
      companyId,
      company,
      companies,
      user,
      setCompanyId,
      reloadCompanies,
      reloadUser,
      logout,
      apiFetch,
      refreshKey,
      bumpRefresh,
    ],
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
}
